import useResizeObserver from '@react-hook/resize-observer';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import _, { throttle } from 'underscore';

import {
  DynamicAssetUpdatedMessage,
  PoiUpdatedMessage,
} from '../../../../../typings/websocket-messages';
import NoContentImage from '../../../../../www/static/empty_state_asset_menu.svg';
import { FeatureFlagsManager } from '../../../../js/core/feature-flags-manager';
import { publish, SubscriptionTopic } from '../../../../js/messaging/pubsub';
import { ProjectStore } from '../../../../js/stores/project-store';
import { ThemeManager } from '../../../../js/theming/theme-manager';
import { trimChars } from '../../../../js/utils/text/text';
import { isDefined } from '../../../../js/utils/variables';
import { AssetManager } from '../../../../js/viewer/asset-manager';
import { Comment } from '../../../../js/viewer/elements/comment';
import { Design } from '../../../../js/viewer/elements/design';
import { ElementObj } from '../../../../js/viewer/elements/element-obj';
import { AssetWithFileInfo } from '../../../../js/viewer/elements/file-info/types';
import { Geometry } from '../../../../js/viewer/elements/geometry';
import { Model } from '../../../../js/viewer/elements/model';
import { Poi } from '../../../../js/viewer/elements/poi';
import { TerrainModel } from '../../../../js/viewer/elements/terrain-model';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { reset } from '../../../utils/styled-reset';
import { IconPanel } from '../../icon-panel/icon-panel';
import { AssetState } from '../state/asset-state';
import { CadGroupVisibilityState } from '../state/cad-group-visibility-state';
import { CategoryContext } from '../state/category-state';
import { CategoryVisibilityState } from '../state/category-visibility-state';
import { CheckBoxState } from '../state/checkbox-state';
import { AreaAsset } from './assets/area/area-asset';
import { CommentAsset } from './assets/comment/comment-asset';
import { DesignAsset } from './assets/design/design-asset';
import { LineAsset } from './assets/line/line-asset';
import { ModelAsset } from './assets/model/model-asset';
import { PoiAsset } from './assets/poi/poi-asset';
import { TerrainModelAsset } from './assets/terrain-model/terrain-model-asset';
import { CadGroup, CadGroupItem } from './components/asset-blocks/cad-group';
import { Category } from './components/asset-blocks/category';
import { VirtualAssetList } from './components/virtual-asset-list';

type Props = {
  assetNameFilter: string;
};

async function getAssetManagerAssets(): Promise<ElementObj[]> {
  const projectId = ProjectStore.instance.project?.id;

  if (!projectId) {
    return [];
  }

  if (AssetManager.instance.isLoaded()) {
    AssetManager.instance.setVisible(true);
    return AssetManager.instance.getAssets();
  }

  await AssetManager.instance.loadProjectAssets(
    projectId,
    AssetManager.instance.isCadViewer ? ['Comment', 'Design', 'Dxf', 'Glb', 'Poi'] : undefined,
  );

  /**
   * In shared view we want line and areas to be displayed automatically.
   *
   * TODO:
   * This step is very slow when there are many (> 100) points on the
   * geometries and needs to be optimized.
   */
  if (ThemeManager.instance.isSharedView) {
    for (const asset of [
      ...AssetManager.instance.getAssetsByType('line'),
      ...AssetManager.instance.getAssetsByType('area'),
    ]) {
      if (asset.getStateVisibility()) {
        asset.loadGeometry();
      }
    }
  }

  AssetManager.instance.setVisible(true);
  return AssetManager.instance.getAssets();
}

function useFetchDynamicAssetMutation() {
  return useMutation({
    mutationFn: (dynamicAssetId: string) =>
      AssetManager.instance.updateAsset(dynamicAssetId, 'DynamicAsset'),
    retry: false,
    networkMode: 'always',
  });
}

function useFetchPoiMutation() {
  return useMutation({
    mutationFn: (poiid: string) => AssetManager.instance.updateAsset(poiid, 'Poi'),
    retry: false,
    networkMode: 'always',
  });
}

function useAssetManagerAssets() {
  const categoryContext = React.useContext(CategoryContext);
  const { buildErrorList, handleError, clearErrors } = useErrorHandling();

  const { mutate: mutateFetchDynamicAsset } = useFetchDynamicAssetMutation();
  const { mutate: mutateFetchPoi } = useFetchPoiMutation();

  const { mutate, data, error } = useMutation({
    mutationFn: getAssetManagerAssets,
    retry: false,
    networkMode: 'always',
    onMutate: () => clearErrors(),
  });

  const throttledMutate = useMemo(() => {
    return throttle(mutate, 500);
  }, [mutate]);

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: throttledMutate });
    }
  }, [error, handleError, throttledMutate]);

  useSubscribe(SubscriptionTopic.AssetListChanged, () => throttledMutate());

  useSubscribe(SubscriptionTopic.WebsocketMessageRecieved, (data) => {
    const isCurrentProject =
      (data as DynamicAssetUpdatedMessage | PoiUpdatedMessage).projectId ===
      ProjectStore.instance.project?.id;
    if (!isCurrentProject) {
      return;
    }

    switch (data.message) {
      case 'DynamicAssetUpdated':
        mutateFetchDynamicAsset(data.dynamicAssetId);
        break;
      case 'PoiUpdated':
        mutateFetchPoi(data.poiId);
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    void categoryContext.refreshNonce;
    throttledMutate();
  }, [categoryContext.refreshNonce, throttledMutate]);

  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);
  const isLoaded = useMemo(() => isDefined(data), [data]);
  const assets = useMemo(() => data ?? [], [data]);

  return {
    assets,
    isLoaded,
    errorList,
  };
}

function filterAndSort<T extends { name: string }>(list: T[], filter: string): T[] {
  return list
    .filter((x) => x.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function groupCadFiles(cadFiles: AssetWithFileInfo[]) {
  return _.chain(cadFiles)
    .groupBy((asset) => {
      const folderId = asset.fileInfo?.folderId;
      const path = asset.fileInfo?.path.substring(0, asset.fileInfo.path.lastIndexOf('/'));

      // Group by folder id and path.
      // Returning object with these as properties isn't working.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `${folderId}#${path}`;
    })
    .map((value, key: string) => {
      const folderId = key.substr(0, key.indexOf('#'));
      const path = key.substr(key.indexOf('#') + 1);

      return {
        folderId,
        path: trimChars(path, ['/', ' ']),
        // @ts-ignore
        assets: _.sortBy(value, (asset) => asset.name.toLowerCase()),
      };
    })
    .sortBy((asset) => asset.path.toLowerCase())
    .value();
}

const AssetMenuList = (props: Props) => {
  const { t } = useTranslation();
  const { assets, errorList, isLoaded } = useAssetManagerAssets();
  const theme = useTheme();

  const categoryRefs = {
    design: React.createRef<HTMLDivElement>(),
    area: React.createRef<HTMLDivElement>(),
    line: React.createRef<HTMLDivElement>(),
    comment: React.createRef<HTMLDivElement>(),
    poi: React.createRef<HTMLDivElement>(),
    cad: React.createRef<HTMLDivElement>(),
    model: React.createRef<HTMLDivElement>(),
    terrainModel: React.createRef<HTMLDivElement>(),
  };
  const [designCategoryHeight, setDesignCategoryHeight] = React.useState(0);
  const [areaCategoryHeight, setAreaCategoryHeight] = React.useState(0);
  const [lineCategoryHeight, setLineCategoryHeight] = React.useState(0);
  const [commentCategoryHeight, setCommentCategoryHeight] = React.useState(0);
  const [poiCategoryHeight, setPoiCategoryHeight] = React.useState(0);
  const [cadCategoryHeight, setCadCategoryHeight] = React.useState(0);
  const [modelCategoryHeight, setModelCategoryHeight] = React.useState(0);
  const [terrainModelCategoryHeight, setTerrainModelCategoryHeight] = React.useState(0);

  const designs = useMemo<Design[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'design'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  const areas = useMemo<Geometry[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'area'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  const lines = useMemo<Geometry[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'line'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  const comments = useMemo<Comment[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'comment'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  const pois = useMemo<Poi[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'poi'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  const cadFiles = useMemo<CadGroupItem[]>(() => {
    return groupCadFiles(
      filterAndSort(
        [
          ...(AssetManager.instance.filterAssetsByType(assets ?? [], 'dxf') as AssetWithFileInfo[]),
          ...(AssetManager.instance.filterAssetsByType(assets ?? [], 'glb') as AssetWithFileInfo[]),
        ],
        props.assetNameFilter,
      ),
    );
  }, [assets, props.assetNameFilter]);
  const models = useMemo<Model[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'model'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);
  const terrainModels = useMemo<TerrainModel[]>(() => {
    return filterAndSort(
      AssetManager.instance.filterAssetsByType(assets ?? [], 'terrain-model'),
      props.assetNameFilter,
    );
  }, [assets, props.assetNameFilter]);

  useResizeObserver(categoryRefs.design, (entry) => {
    setDesignCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.area, (entry) => {
    setAreaCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.line, (entry) => {
    setLineCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.comment, (entry) => {
    setCommentCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.poi, (entry) => {
    setPoiCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.cad, (entry) => {
    setCadCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.model, (entry) => {
    setModelCategoryHeight(entry.borderBoxSize[0].blockSize);
  });
  useResizeObserver(categoryRefs.terrainModel, (entry) => {
    setTerrainModelCategoryHeight(entry.borderBoxSize[0].blockSize);
  });

  const isPoiFlagActive = FeatureFlagsManager.instance.isEnabled('SK-6092-POI');
  const noAssets =
    designs.length === 0 &&
    areas.length === 0 &&
    lines.length === 0 &&
    (isPoiFlagActive ? pois.length : comments.length) === 0 &&
    cadFiles.length === 0 &&
    models.length === 0 &&
    terrainModels.length === 0;

  if (errorList) {
    return errorList;
  }

  if (!isLoaded) {
    return (
      <Component>
        <NoContent>
          <IconPanel
            header={{
              icon: { icon: 'spinner', spin: true, size: '3x', color: theme.color.gray.medium },
            }}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('emptyState.loading.title', { ns: 'skyviewAssetMenu' }),
            }}
          </IconPanel>
        </NoContent>
      </Component>
    );
  }

  if (noAssets) {
    return (
      <Component>
        <NoContent>
          {props.assetNameFilter.length === 0 ? (
            <IconPanel header={<NoContentImage />} responsiveness={{ compressed: false }}>
              {{
                title: t('emptyState.noSearchText.title', { ns: 'skyviewAssetMenu' }),
                info: (
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="emptyState.noSearchText.info"
                    ns="skyviewAssetMenu"
                  />
                ),
              }}
            </IconPanel>
          ) : (
            <IconPanel header={<NoContentImage />} responsiveness={{ compressed: false }}>
              {{
                title: t('emptyState.searchText.title', { ns: 'skyviewAssetMenu' }),
                info: t('emptyState.searchText.info', { ns: 'skyviewAssetMenu' }),
              }}
            </IconPanel>
          )}
        </NoContent>
      </Component>
    );
  }

  return (
    <Component>
      {designs.length > 0 && (
        <CheckBoxState assets={designs} categoryType="design">
          <CategoryVisibilityState
            assets={designs}
            categoryType="design"
            height={designCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.design}
              icon={{ icon: ['fal', 'pen-ruler'], fixedWidth: true }}
              title={t('design.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {designs.map((x) => (
                <AssetState key={`design-${x.uuid}`}>
                  <DesignAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {areas.length > 0 && (
        <CheckBoxState assets={areas} categoryType="area">
          <CategoryVisibilityState assets={areas} categoryType="area" height={areaCategoryHeight}>
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.area}
              icon={{ icon: ['fal', 'draw-polygon'], fixedWidth: true }}
              title={t('area.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {areas.map((x) => (
                <AssetState key={`area-${x.uuid}`}>
                  <AreaAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {lines.length > 0 && (
        <CheckBoxState assets={lines} categoryType="line">
          <CategoryVisibilityState assets={lines} categoryType="line" height={lineCategoryHeight}>
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.line}
              icon={{ icon: ['fal', 'wave-triangle'], fixedWidth: true }}
              title={t('line.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {lines.map((x) => (
                <AssetState key={`line-${x.uuid}`}>
                  <LineAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {comments.length > 0 && !isPoiFlagActive && (
        <CheckBoxState assets={comments} categoryType="comment">
          <CategoryVisibilityState
            assets={comments}
            categoryType="comment"
            height={commentCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.comment}
              icon={{ icon: ['fal', 'location-dot'], fixedWidth: true }}
              title={t('comment.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {comments.map((x) => (
                <AssetState key={`comment-${x.uuid}`}>
                  <CommentAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {pois.length > 0 && isPoiFlagActive && (
        <CheckBoxState assets={pois} categoryType="poi">
          <CategoryVisibilityState assets={pois} categoryType="poi" height={poiCategoryHeight}>
            <Category
              collapseTactic="shrink"
              headerRef={categoryRefs.poi}
              icon={{ icon: ['fal', 'location-dot'], fixedWidth: true }}
              title={'POI'}
            >
              <VirtualAssetList
                assetType="poi"
                getAssetId={(p: Poi) => p.assetId!}
                items={pois}
                maxHeight={'50em'}
                onScrolledIntoIndex={(poi: Poi) =>
                  // We need to push 'OpenPoiPreviewDialog' to ensure that elements that are scrolled to
                  // open the image dialog.
                  //
                  // This because while the element is outside of the VirtualAssetList viewport,
                  // it does not listen to topics. So we need to push our desired topic to the React element.
                  queueMicrotask(() =>
                    setTimeout(() => publish(SubscriptionTopic.OpenPoiPreviewDialog, poi), 100),
                  )
                }
              >
                {(item: Poi) => (
                  <AssetState key={`pois-${item.uuid}`}>
                    <PoiAsset asset={item} />
                  </AssetState>
                )}
              </VirtualAssetList>
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {cadFiles.length > 0 && (
        <CheckBoxState assets={cadFiles.flatMap((x) => x.assets)} categoryType="cad">
          <CategoryVisibilityState
            assets={cadFiles.flatMap((x) => x.assets)}
            categoryType="cad"
            height={cadCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.cad}
              icon={{ icon: ['fal', 'cubes'], fixedWidth: true }}
              title={t('cad.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {cadFiles.map((x) => {
                return (
                  <CadGroupVisibilityState assets={x.assets} folderId={x.folderId} key={x.folderId}>
                    <CadGroup cadGroup={x} name={x.path} stickyTopPos={cadCategoryHeight} />
                  </CadGroupVisibilityState>
                );
              })}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {models.length > 0 && (
        <CheckBoxState assets={models} categoryType="model">
          <CategoryVisibilityState
            assets={models}
            categoryType="model"
            height={modelCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.model}
              icon={{ icon: ['fal', 'cube'], fixedWidth: true }}
              title={t('model.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {models.map((x) => (
                <AssetState key={`model-${x.uuid}`}>
                  <ModelAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {terrainModels.length > 0 && (
        <CheckBoxState assets={terrainModels} categoryType="terrain-model">
          <CategoryVisibilityState
            assets={terrainModels}
            categoryType="terrain-model"
            height={terrainModelCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.terrainModel}
              icon={{ icon: ['fal', 'mountains'], fixedWidth: true }}
              title={t('terrainModel.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {terrainModels.map((x) => (
                <AssetState key={`terrain-model-${x.uuid}`}>
                  <TerrainModelAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}
    </Component>
  );
};

const Component = styled.div`
  ${reset}

  display: flex;
  flex-direction: column;
  gap: 1em;

  overflow: auto;
`;

const NoContent = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
`;

AssetMenuList.styled = Component;

export { AssetMenuList, Props as AssetMenuListProps };
